/**
 * Đề xuất của "Dò mã & quan hệ" và cách ghép chúng vào bản khai — phần thuần của `NguonSyncTab.tsx` native.
 */
import type { Finding } from '../types.ts';

/** Khoá ổn định cho một đề xuất — để nhớ cái nào đã ghi. */
export function khoaDeXuat(f: Finding): string {
  return `${f.kind}:${f.table}:${f.column ?? ''}:${JSON.stringify(f.patch).slice(0, 60)}`;
}

/**
 * Thứ tự các nhóm đề xuất — QUAN HỆ TRƯỚC. Nút mang tên "Dò mã & quan hệ" và người ta bấm nó để nối bảng; cột `enum`
 * bao giờ cũng nhiều hơn quan hệ hàng chục lần, xếp lẫn thì thứ người dùng tới vì nó nằm ở trang thứ ba. Bốn nhóm cũng
 * là bốn VIỆC khác nhau: nối bảng · dạy danh sách giá trị · tắt bảng chết · đi sửa một chỗ lệch.
 */
export const NHOM_DE_XUAT = [
  { ma: 'quanHe', kinds: ['link'] },
  { ma: 'giaTri', kinds: ['enum'] },
  { ma: 'bangChet', kinds: ['dead_table'] },
  { ma: 'soLech', kinds: ['row_count_drift'] },
] as const;

export type MaNhomDeXuat = (typeof NHOM_DE_XUAT)[number]['ma'];

/** Độ chắc chắn nói thành lời: người xác nhận cần biết nên đọc kỹ tới đâu, không cần biết thang điểm. */
export function mucChac(confidence: number): 'cao' | 'vua' | 'thap' {
  return confidence >= 0.8 ? 'cao' : confidence >= 0.5 ? 'vua' : 'thap';
}

interface BangKhai {
  name?: string;
  enabled?: boolean;
  note?: string;
  links?: { column: string; toTable: string; toColumn: string; kind?: string }[];
  columns?: { name: string; enumValues?: unknown }[];
  [k: string]: unknown;
}

/**
 * Ghép một đề xuất vào bản khai hiện có — KHÔNG phải `{ ...t, ...f.patch }`. Ba loại vá ba chỗ khác nhau, và trải phẳng
 * hỏng cả ba theo ba kiểu:
 *   · `link` — `patch.links` mang ĐÚNG MỘT quan hệ; trải phẳng là gán đè cả mảng, bảng ba khoá ngoại chỉ còn một.
 *   · `enum` — `patch.enumValues` thuộc về MỘT CỘT; đặt lên bảng là 422 (`extra="forbid"`).
 *   · `dead_table` — giữ `note`, tức câu trả lời "vì sao bảng này bị tắt".
 * Trả về cả mảng: đường ghi `saveTables` ghi đè, gửi một phần là xoá phần còn lại.
 */
export function ghepDeXuat(tables: Record<string, unknown>[], f: Finding): Record<string, unknown>[] {
  return tables.map((x) => {
    const b = x as BangKhai;
    if (b.name !== f.table) return x;
    if (f.kind === 'link') {
      const them = (f.patch.links ?? []) as NonNullable<BangKhai['links']>;
      const daCo = b.links ?? [];
      // Lọc trùng theo (cột, bảng cha, cột cha): bấm Ghi hai lần không sinh hai cạnh chồng nhau trên sơ đồ.
      const moi = them.filter(
        (l) => !daCo.some((c) => c.column === l.column && c.toTable === l.toTable && c.toColumn === l.toColumn),
      );
      return { ...b, links: [...daCo, ...moi] };
    }
    if (f.kind === 'enum' && f.column) {
      return {
        ...b,
        columns: (b.columns ?? []).map((c) => (c.name === f.column ? { ...c, enumValues: f.patch.enumValues } : c)),
      };
    }
    if (f.kind === 'dead_table') {
      return { ...b, enabled: false, ...(f.patch.note ? { note: f.patch.note } : {}) };
    }
    return x;
  });
}
